import React from "react";
import axios from "axios";
import { Star, MessageCircle } from "lucide-react";
import { MESSENGER_URL } from "@/data/staycation";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 12;

const METRO_MANILA = ["Manila", "Quezon City", "Pasig", "Makati", "Caloocan", "Pasay", "Valenzuela", "Mandaluyong", "Las Piñas", "Las Pinas", "Taguig", "Parañaque", "City of Manila", "Marikina", "Muntinlupa", "San Juan", "Navotas", "Malabon"];
const VISAYAS = ["Cebu", "Mandaue", "Iloilo", "Bacolod", "Roxas City", "Tacloban", "Dumaguete"];
const MINDANAO = ["Davao", "Cagayan de Oro", "Zamboanga", "Iligan", "General Santos", "Butuan"];
const LUZON_OTHER = ["Cavite", "Laguna", "Antipolo", "Dasmariñas", "Biñan", "Mabalacat", "Imus", "Naga", "Legazpi", "Baguio", "Taytay", "Santo Tomas", "Bulacan", "Pampanga", "General Mariano Alvarez", "Batangas", "Rizal", "Tarlac", "Nueva Ecija"];

function regionOf(loc = "") {
  const s = loc.toLowerCase();
  if (!s || s === "philippines") return "ph-other";
  const has = (list) => list.some((c) => s.includes(c.toLowerCase()));
  if (has(METRO_MANILA)) return "metro-manila";
  if (has(VISAYAS)) return "visayas";
  if (has(MINDANAO)) return "mindanao";
  if (has(LUZON_OTHER)) return "luzon";
  if (s.includes("philippines")) return "ph-other";
  return "international";
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "metro-manila", label: "Metro Manila" },
  { key: "luzon", label: "Luzon" },
  { key: "visayas", label: "Visayas" },
  { key: "mindanao", label: "Mindanao" },
  { key: "international", label: "International" },
];

function Stars({ n }) {
  return (
    <div className="flex items-center gap-0.5 text-[var(--xs-primary)]">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`h-4 w-4 ${i < n ? "fill-current" : "opacity-30"}`} />
      ))}
    </div>
  );
}

function avatarUrl(name) {
  const safe = encodeURIComponent((name || "Guest").trim());
  // Deterministic colored avatar with initials — free, no signup
  return `https://ui-avatars.com/api/?name=${safe}&size=80&background=FF385C&color=fff&bold=true&format=png`;
}

function ReviewCard({ r }) {
  const initial = (r.name || "G").charAt(0);
  const handleImgError = (e) => {
    // Hide broken image and let the fallback initial-circle take its place
    e.currentTarget.style.display = "none";
    const fb = e.currentTarget.nextElementSibling;
    if (fb) fb.style.display = "inline-flex";
  };
  return (
    <article className="xs-card p-6 flex flex-col h-full" data-testid={`review-card-${r.id}`}>
      <Stars n={r.rating} />
      <p className="mt-4 text-[15px] text-neutral-800 leading-relaxed flex-1">“{r.comment}”</p>
      <div className="mt-6 flex items-center gap-3 pt-5 border-t border-neutral-100">
        <img
          src={avatarUrl(r.name)}
          alt={r.name}
          width="40"
          height="40"
          loading="lazy"
          decoding="async"
          onError={handleImgError}
          data-testid={`review-avatar-${r.id}`}
          className="h-10 w-10 rounded-full object-cover shrink-0 border border-neutral-100"
        />
        <div
          aria-hidden="true"
          style={{ display: "none" }}
          className="h-10 w-10 rounded-full bg-[var(--xs-primary)] text-white inline-flex items-center justify-center font-bold shrink-0"
        >
          {initial}
        </div>
        <div>
          <p className="text-sm font-semibold leading-tight">{r.name}</p>
          <p className="text-xs text-neutral-500">{r.location} • {r.date}</p>
          <p className="text-[11px] text-[var(--xs-primary)] font-semibold mt-0.5">{r.stay}</p>
        </div>
      </div>
    </article>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [filter, setFilter] = React.useState("all");
  const [visible, setVisible] = React.useState(PAGE_SIZE);

  React.useEffect(() => {
    let active = true;
    axios.get(`${API}/reviews`)
      .then((r) => active && setReviews(r.data))
      .catch((e) => active && setError(e?.message || "Failed to load reviews"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "5.0";

  const counts = React.useMemo(() => {
    const map = { all: reviews.length };
    FILTERS.slice(1).forEach((f) => { map[f.key] = 0; });
    reviews.forEach((r) => { const k = regionOf(r.location); if (map[k] !== undefined) map[k] += 1; });
    return map;
  }, [reviews]);

  const filtered = React.useMemo(() => {
    if (filter === "all") return reviews;
    return reviews.filter((r) => regionOf(r.location) === filter);
  }, [reviews, filter]);

  const onPickFilter = (k) => { setFilter(k); setVisible(PAGE_SIZE); };

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  return (
    <div data-testid="reviews-page">
      <Seo
        title="Guest Reviews — 333 verified 5-star reviews"
        description="Read 333 verified 5-star reviews from real guests of Xanders Staycations. Hosted by Airbnb Superhost Alexander — responsive, accommodating, and treats every guest like family."
        path="/reviews"
      />
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pt-14 pb-10 grid lg:grid-cols-3 gap-8 items-end">
        <div className="lg:col-span-2">
          <p className="xs-eyebrow">Guest Reviews</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-2 max-w-2xl leading-[1.05]">Loved by hundreds of happy guests.</h1>
          <p className="mt-4 text-neutral-600 max-w-xl">Real stories from real staycationers. Every review below is from a verified booking made through Airbnb or Messenger.</p>
        </div>
        <div className="xs-card p-6 text-center" data-testid="reviews-summary">
          <Stars n={5} />
          <p className="font-display text-5xl font-black mt-2">{avg}<span className="text-2xl text-neutral-400">/5</span></p>
          <p className="text-sm text-neutral-600 mt-1">Average across {reviews.length || 6} guest reviews</p>
        </div>
      </section>

      {!loading && !error && reviews.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-6" data-testid="reviews-filters">
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((f) => {
              const count = counts[f.key] || 0;
              const active = filter === f.key;
              const disabled = f.key !== "all" && count === 0;
              return (
                <button
                  key={f.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => onPickFilter(f.key)}
                  data-testid={`filter-chip-${f.key}`}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                    active
                      ? "bg-[var(--xs-primary)] text-white border-[var(--xs-primary)]"
                      : disabled
                        ? "bg-neutral-50 text-neutral-400 border-neutral-200 cursor-not-allowed"
                        : "bg-white text-neutral-700 border-neutral-200 hover:border-[var(--xs-primary)] hover:text-[var(--xs-primary)]"
                  }`}
                >
                  {f.label}
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white/25" : "bg-neutral-100 text-neutral-600"}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-16">
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="xs-card p-6 animate-pulse h-56" />
            ))}
          </div>
        )}
        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700" data-testid="reviews-error">
            Could not load reviews: {error}
          </div>
        )}
        {!loading && !error && (
          <>
            {filtered.length === 0 ? (
              <div className="xs-card p-10 text-center" data-testid="reviews-empty">
                <p className="text-neutral-600">No reviews from this region yet — check back soon!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
                {shown.map((r) => <ReviewCard key={r.id} r={r} />)}
              </div>
            )}

            {hasMore && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  data-testid="reviews-load-more"
                  className="inline-flex items-center gap-2 rounded-full border border-neutral-300 bg-white hover:border-[var(--xs-primary)] hover:text-[var(--xs-primary)] px-6 py-3 text-sm font-semibold transition-colors"
                >
                  Load more reviews
                  <span className="text-xs text-neutral-500">({filtered.length - visible} remaining)</span>
                </button>
              </div>
            )}
            {!hasMore && filtered.length > PAGE_SIZE && (
              <p className="mt-10 text-center text-sm text-neutral-500" data-testid="reviews-end">
                You've seen all {filtered.length} reviews{filter !== "all" ? " in this region" : ""}.
              </p>
            )}
          </>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-24">
        <div className="rounded-3xl bg-[var(--xs-primary)] text-white p-10 sm:p-14 text-center">
          <h3 className="font-display text-3xl sm:text-4xl font-black">Be our next happy guest.</h3>
          <p className="mt-3 max-w-xl mx-auto text-white/90">Tap below to chat with Kuya Alex on Messenger and lock in your dates.</p>
          <a
            href={MESSENGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-cta-messenger"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-[var(--xs-primary)] hover:bg-white/90 px-6 py-3 text-sm font-semibold transition-colors"
          >
            <MessageCircle className="h-4 w-4" /> Book via Messenger
          </a>
        </div>
      </section>
    </div>
  );
}
